import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { orderAPI } from '../../api';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import { EmptyState } from '../../components/common/Misc';
import styles from './PlaceOrderPage.module.css';
import { GST_RATE } from '../../utils/pricing';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
const resolveImg = (url) => (url && url.startsWith('http') ? url : `${API_BASE}${url}`);


const PlaceOrderPage = () => {
  const { items, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [form, setForm] = useState({
    name:     user?.name  || '',
    email:    user?.email || '',
    phone:    user?.phone || '',
    altPhone: '',
  });
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    setErrors(er => ({ ...er, [k]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())                      e.name  = 'Full name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email is required';
    if (!form.phone.trim() || form.phone.length < 7) e.phone = 'Valid phone number is required';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) return setErrors(errs);

    setLoading(true);
    try {
      await orderAPI.place({
        customer: {
          name:     form.name.trim(),
          email:    form.email.trim(),
          phone:    form.phone.trim(),
          altPhone: form.altPhone.trim() || null,
        },
        items: items.map(item => ({
          bike_id:    item.bike._id,
          start_time: item.startTime,
          end_time:   item.endTime,
          promo_code: item.promoCode || undefined,
        })),
      });

      clearCart();
      setSuccess(true);
      toast.success('Order placed! A confirmation has been sent.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  //  Empty cart guard 
  if (items.length === 0 && !success) return (
    <div className="container">
      <EmptyState icon="🛒" title="Your cart is empty" subtitle="Add bikes to your cart before placing an order" />
      <div style={{ textAlign: 'center', marginTop: '1rem' }}>
        <Link to="/bikes"><Button>Browse bikes</Button></Link>
      </div>
    </div>
  );

  //  Success screen 
  if (success) return (
    <div className={styles.successWrap}>
      <div className={styles.successCard}>
        <div className={styles.successIcon}>✅</div>
        <h1 className={styles.successTitle}>Order confirmed!</h1>
        <p className={styles.successSub}>
          Thank you, <strong>{form.name}</strong>. Your booking details have been received and a confirmation email has been sent to our team.
        </p>
        <p className={styles.successNote}>We'll contact you at <strong>{form.phone}</strong> to finalize your ride.</p>
        <div className={styles.successBtns}>
          <Button onClick={() => navigate('/bookings')}>View my bookings</Button>
          <Button variant="ghost" onClick={() => navigate('/bikes')}>Book another</Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container">
      {/* Page header */}
      <div className={styles.pageHeader}>
        <Link to="/cart" className={styles.back}>← Back to cart</Link>
        <h1 className={styles.pageTitle}>Place your order</h1>
        <p className={styles.pageSub}>Fill in your contact details and confirm your booking</p>
      </div>

      <div className={styles.layout}>
        {/*  Left: customer form  */}
        <div className={styles.formSide}>
          <div className={styles.formCard}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.stepBadge}>1</span>
              Contact details
            </h2>

            <form onSubmit={handleSubmit} className={styles.form} noValidate>
              <Input
                label="Full name"
                type="text"
                placeholder="John Doe"
                value={form.name}
                onChange={set('name')}
                error={errors.name}
              />
              <Input
                label="Email address"
                type="email"
                placeholder="john@example.com"
                value={form.email}
                onChange={set('email')}
                error={errors.email}
              />
              <div className={styles.row}>
                <Input
                  label="Phone number"
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={form.phone}
                  onChange={set('phone')}
                  error={errors.phone}
                />
                <Input
                  label="Alternative phone (optional)"
                  type="tel"
                  placeholder="+91 98765 43211"
                  value={form.altPhone}
                  onChange={set('altPhone')}
                />
              </div>

              <div className={styles.notice}>
                <span>📧</span>
                <p>Your booking confirmation will be sent to our team. We'll reach out to you shortly to confirm your ride details.</p>
              </div>

              <Button type="submit" fullWidth size="lg" loading={loading}>
                Confirm booking
              </Button>
            </form>
          </div>
        </div>

        {/*  Right: order summary  */}
        <div className={styles.summarySide}>
          <div className={styles.summaryCard}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.stepBadge}>2</span>
              Your bikes
            </h2>

            <div className={styles.bikeList}>
              {items.map((item, i) => (
                <div key={item.bike._id} className={styles.bikeItem}>
                  <div className={styles.bikeImg}>
                    {item.bike.images?.[0]
                      ? <img src={resolveImg(item.bike.images[0])} alt={item.bike.name} />
                      : <span>🏍️</span>
                    }
                  </div>
                  <div className={styles.bikeInfo}>
                    <div className={styles.bikeTop}>
                      <h3 className={styles.bikeName}>{item.bike.name}</h3>
                      <span className={styles.bikePrice}>₹{item.finalAmount.toFixed(2)}</span>
                    </div>
                    <p className={styles.bikeMeta}>{item.bike.brand} · <span className={styles.bikeType}>{item.bike.type}</span></p>
                    <div className={styles.bikeDates}>
                      <div className={styles.dateRow}>
                        <span className={styles.dateLabel}>From</span>
                        <span className={styles.dateVal}>{format(new Date(item.startTime), 'dd MMM yyyy, hh:mm a')}</span>
                      </div>
                      <div className={styles.dateRow}>
                        <span className={styles.dateLabel}>To</span>
                        <span className={styles.dateVal}>{format(new Date(item.endTime), 'dd MMM yyyy, hh:mm a')}</span>
                      </div>
                      <div className={styles.dateRow}>
                        <span className={styles.dateLabel}>Duration</span>
                        <span className={styles.dateVal}>{item.days} day{item.days > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    {item.promoCode && (
                      <div className={styles.promoTag}>
                        🏷 {item.promoCode}  saving ₹{item.discountAmount.toFixed(2)}
                      </div>
                    )}
                    {item.discountAmount > 0 && (
                      <div className={styles.strikeRow}>
                        <span>Original price</span>
                        <span className={styles.strike}>₹{item.totalAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className={styles.strikeRow}>
                      <span>GST (18%)</span>
                      <span>₹{item.gst ? item.gst.toFixed(2) : (item.afterDiscount * GST_RATE).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Grand total */}
            <div className={styles.totalBox}>
              <span className={styles.totalLabel}>Grand total</span>
              <span className={styles.totalAmount}>₹{cartTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaceOrderPage;
